import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from './Navbar';
import Footer from './Footer';

const serviceDetails = {
  'AI & Machine Learning': {
    tagline: 'Intelligent Systems That Think, Learn & Act',
    whatWeDeliver: [
      'Custom AI Chatbots & Virtual Assistants',
      'Predictive Analytics & Forecasting Models',
      'Computer Vision & Image Recognition Systems',
      'Natural Language Processing (NLP) Pipelines',
      'Intelligent Process Automation (IPA)',
      'Recommendation Engines & Personalisation',
      'ML Model Training, Deployment & Monitoring',
    ],
    expertise: [
      { label: 'Frameworks', value: 'TensorFlow, PyTorch, Scikit-learn, Hugging Face' },
      { label: 'Languages', value: 'Python, R, Julia' },
      { label: 'Cloud AI', value: 'AWS SageMaker, Google Vertex AI, Azure ML' },
      { label: 'Domains', value: 'Healthcare, Finance, Retail, Manufacturing, EdTech' },
      { label: 'Models', value: 'LLMs, CNNs, RNNs, Transformers, GANs' },
    ],
  },
  'Mobile App Development': {
    tagline: 'Apps That Users Love — From MVP to Enterprise Scale',
    whatWeDeliver: [
      'Cross-Platform Apps (React Native, Flutter)',
      'Native iOS & Android Development',
      'UI/UX-First App Design & Prototyping',
      'Offline-First & PWA Mobile Solutions',
      'App Store Deployment & Optimisation',
      'Push Notifications & Real-Time Features',
      'Integration with Payment, Maps & Third-Party APIs',
    ],
    expertise: [
      { label: 'Cross-Platform', value: 'React Native, Flutter, Expo' },
      { label: 'Native', value: 'Swift (iOS), Kotlin (Android)' },
      { label: 'Backend', value: 'Node.js, Firebase, Supabase' },
      { label: 'State Mgmt', value: 'Redux, Zustand, Riverpod' },
      { label: 'Domains', value: 'FinTech, HealthTech, EdTech, E-Commerce' },
    ],
  },
  'Web Development': {
    tagline: 'Blazing-Fast, Beautiful Web Experiences Built to Convert',
    whatWeDeliver: [
      'Full-Stack Web Application Development',
      'E-Commerce Platforms & Custom Portals',
      'Progressive Web Apps (PWA)',
      'CMS-Powered Websites (Headless & Traditional)',
      'REST & GraphQL API Development',
      'Performance Optimisation & Core Web Vitals',
      'SEO-Ready Architecture & Technical Audits',
    ],
    expertise: [
      { label: 'Frontend', value: 'React, Next.js, Vue, Svelte, Tailwind CSS' },
      { label: 'Backend', value: 'Node.js, Django, FastAPI, Laravel' },
      { label: 'Databases', value: 'PostgreSQL, MongoDB, MySQL, Redis' },
      { label: 'CMS', value: 'Sanity, Contentful, Strapi, WordPress' },
      { label: 'DevOps', value: 'Vercel, Netlify, AWS, Docker' },
    ],
  },
  'Cloud & DevOps': {
    tagline: 'Scalable Infrastructure That Never Sleeps',
    whatWeDeliver: [
      'Cloud Architecture Design & Migration',
      'CI/CD Pipeline Setup & Automation',
      'Containerisation with Docker & Kubernetes',
      'Infrastructure as Code (Terraform, Ansible)',
      'Cloud Cost Optimisation Strategies',
      'Monitoring, Alerting & Observability',
      'Multi-Cloud & Hybrid Cloud Solutions',
    ],
    expertise: [
      { label: 'Cloud Providers', value: 'AWS, Google Cloud, Microsoft Azure' },
      { label: 'Containers', value: 'Docker, Kubernetes, Helm' },
      { label: 'IaC', value: 'Terraform, Pulumi, AWS CDK, Ansible' },
      { label: 'CI/CD', value: 'GitHub Actions, Jenkins, GitLab CI, CircleCI' },
      { label: 'Observability', value: 'Prometheus, Grafana, Datadog, ELK Stack' },
    ],
  },
  'UI/UX Design': {
    tagline: 'Design That Delights — Every Pixel, Every Interaction',
    whatWeDeliver: [
      'User Research & Persona Development',
      'Information Architecture & Wireframing',
      'High-Fidelity Prototypes & Mockups',
      'Design Systems & Component Libraries',
      'Usability Testing & Heuristic Evaluation',
      'Motion Design & Micro-Interaction Specification',
      'Accessibility-First (WCAG 2.1) Design',
    ],
    expertise: [
      { label: 'Design Tools', value: 'Figma, Adobe XD, Sketch, Illustrator' },
      { label: 'Prototyping', value: 'Figma, ProtoPie, Framer, InVision' },
      { label: 'Research', value: 'User Interviews, A/B Testing, Heatmaps' },
      { label: 'Motion', value: 'Lottie, After Effects, Framer Motion' },
      { label: 'Standards', value: 'WCAG 2.1, Material Design, HIG' },
    ],
  },
  'Cybersecurity & QA': {
    tagline: 'Secure, Tested & Battle-Hardened Software',
    whatWeDeliver: [
      'Penetration Testing & Vulnerability Assessments',
      'Security Code Review & Audits',
      'Automated Test Suite Development (Unit, Integration, E2E)',
      'Performance & Load Testing',
      'OWASP Top-10 Compliance Checks',
      'DevSecOps Pipeline Integration',
      'Incident Response & Risk Consulting',
    ],
    expertise: [
      { label: 'Pen Testing', value: 'Burp Suite, Metasploit, Nmap, OWASP ZAP' },
      { label: 'Test Automation', value: 'Selenium, Cypress, Playwright, Jest' },
      { label: 'Performance', value: 'JMeter, k6, Locust' },
      { label: 'Standards', value: 'ISO 27001, SOC 2, GDPR, HIPAA' },
      { label: 'Domains', value: 'FinTech, Healthcare, Government, SaaS' },
    ],
  },
  'Data Engineering': {
    tagline: 'Turn Raw Data Into Your Greatest Competitive Advantage',
    whatWeDeliver: [
      'End-to-End Data Pipeline Design & Development',
      'Data Warehousing & Lakehouse Architecture',
      'Real-Time Streaming Data Platforms',
      'Business Intelligence Dashboards & Reports',
      'Data Quality, Governance & Cataloguing',
      'ETL/ELT Pipeline Automation',
      'Custom KPI Tracking & Visualisation Tools',
    ],
    expertise: [
      { label: 'Pipelines', value: 'Apache Spark, Kafka, Airflow, dbt' },
      { label: 'Warehousing', value: 'Snowflake, BigQuery, Redshift, Databricks' },
      { label: 'BI Tools', value: 'Power BI, Tableau, Metabase, Looker' },
      { label: 'Storage', value: 'S3, Delta Lake, Parquet, PostgreSQL' },
      { label: 'Streaming', value: 'Apache Flink, Kafka Streams, Pub/Sub' },
    ],
  },
  'Embedded & IoT': {
    tagline: 'Where Physical Meets Digital — Smart Systems for a Connected World',
    whatWeDeliver: [
      'Custom Firmware & Embedded Software Development',
      'IoT Platform Architecture & Device Management',
      'Sensor Integration & Real-Time Data Processing',
      'Edge Computing Solutions',
      'Industrial Automation & SCADA Systems',
      'OTA Update Infrastructure',
      'PCB Design Consultation & Prototyping Support',
    ],
    expertise: [
      { label: 'Microcontrollers', value: 'STM32, ESP32, Arduino, Raspberry Pi' },
      { label: 'Protocols', value: 'MQTT, CoAP, BLE, Zigbee, LoRaWAN, CAN Bus' },
      { label: 'RTOS', value: 'FreeRTOS, Zephyr, Mbed OS' },
      { label: 'IoT Platforms', value: 'AWS IoT Core, Azure IoT Hub, ThingsBoard' },
      { label: 'Languages', value: 'C, C++, MicroPython, Rust' },
    ],
  },
  'Digital Marketing Tech': {
    tagline: 'Technology-Powered Marketing That Drives Real Growth',
    whatWeDeliver: [
      'SEO-Optimised Platform Architecture',
      'Marketing Automation Workflows & Tools',
      'CRM Integration & Customer Data Platforms',
      'Performance Analytics Dashboards',
      'Email & SMS Marketing Tech Stack Setup',
      'Ad Tech Integrations (Google Ads, Meta)',
      'Conversion Rate Optimisation (CRO) Tools',
    ],
    expertise: [
      { label: 'CRM/CDP', value: 'HubSpot, Salesforce, Segment, Klaviyo' },
      { label: 'Analytics', value: 'GA4, Mixpanel, Amplitude, Hotjar' },
      { label: 'Automation', value: 'Zapier, n8n, ActiveCampaign, Braze' },
      { label: 'SEO Tools', value: 'SEMrush, Ahrefs, Screaming Frog, Search Console' },
      { label: 'Ad Tech', value: 'Google Ads API, Meta Marketing API, DV360' },
    ],
  },
};

export default function ServiceDetail({ service, onClose }) {
  const details = serviceDetails[service.title] || {};

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="fixed inset-0 z-[200] bg-white overflow-y-auto"
    >
      <div className="relative z-50"><Navbar forceOpaque={true} /></div>

      {/* Hero Banner */}
      <div className="relative h-[55vh] min-h-[360px] w-full overflow-hidden">
        {service.bgImage && (
          <motion.img
            src={service.bgImage}
            alt={service.title}
            initial={{ scale: 1.12 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.4, ease: 'easeOut' }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/20 via-slate-900/65 to-slate-900" />

        <motion.button
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          onClick={onClose}
          className="absolute top-24 left-6 md:left-12 flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white font-space text-sm font-semibold hover:bg-white/25 transition-colors"
        >
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
          </svg>
          Back to Services
        </motion.button>

        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-16 pb-12">
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.25, duration: 0.75 }}>
            <span className={"inline-block px-4 py-1.5 rounded-full text-xs font-space font-bold uppercase tracking-widest mb-4 text-white bg-gradient-to-r " + service.color + " shadow-lg"}>
              QuBright Service
            </span>
            <h1 className="font-syne font-extrabold text-4xl md:text-6xl text-white leading-tight mb-3">{service.title}</h1>
            {details.tagline && <p className="font-space text-lg text-slate-300 max-w-2xl">{details.tagline}</p>}
          </motion.div>
        </div>
      </div>

      {/* Body */}
      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-16 py-20">
          <div className="grid lg:grid-cols-5 gap-14">

            {/* Left */}
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.45 }} className="lg:col-span-3">
              <h2 className="font-syne font-extrabold text-3xl text-slate-900 mb-2">
                What We <span className="gradient-text-blue">Deliver</span>
              </h2>
              <p className="font-inter text-slate-500 mb-8 leading-relaxed text-base">{service.desc}</p>
              <ul className="space-y-4">
                {(details.whatWeDeliver || []).map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ x: -24, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.55 + i * 0.07 }}
                    className="flex items-start gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-brand-blue/25 hover:bg-blue-50/30 transition-all duration-300 group"
                  >
                    <div className={"w-8 h-8 rounded-xl bg-gradient-to-br " + service.color + " flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300"}>
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="font-inter text-slate-700 font-medium leading-relaxed pt-0.5">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Right */}
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.55 }} className="lg:col-span-2 space-y-6">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-card overflow-hidden">
                <div className={"bg-gradient-to-r " + service.color + " px-6 py-5"}>
                  <h3 className="font-syne font-bold text-xl text-white">Our Expertise</h3>
                  <p className="font-inter text-white/75 text-sm mt-1">Technologies and tools we master</p>
                </div>
                <div className="divide-y divide-slate-100">
                  {(details.expertise || []).map((item, i) => (
                    <div key={i} className="px-6 py-4">
                      <span className="font-space text-[10px] text-slate-400 uppercase tracking-widest">{item.label}</span>
                      <p className="font-inter text-sm text-slate-700 font-medium leading-relaxed mt-0.5">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl bg-gradient-to-br from-brand-blue to-indigo-700 p-8 text-white shadow-royal">
                <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center mb-5">
                  <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="font-syne font-bold text-2xl mb-3">Ready to Build?</h3>
                <p className="font-inter text-white/75 text-sm leading-relaxed mb-6">
                  Let's discuss your project and craft the perfect solution for your goals.
                </p>
                <a href="#contact" onClick={onClose} className="block text-center bg-white text-brand-blue font-space font-bold text-sm py-3.5 rounded-2xl hover:bg-blue-50 transition-colors shadow-lg">
                  Get in Touch
                </a>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Why QuBright */}
        <div className="bg-slate-50 border-t border-slate-100">
          <div className="max-w-6xl mx-auto px-6 md:px-16 py-16">
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.9 }} className="text-center mb-10">
              <h2 className="font-syne font-extrabold text-3xl text-slate-900">Why <span className="gradient-text">QuBright?</span></h2>
              <p className="font-inter text-slate-500 mt-2 text-base">What sets us apart as your technology partner</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { icon: '🏆', title: 'Proven Track Record', desc: '50+ projects delivered across domains and industries.' },
                { icon: '⚡', title: 'Rapid Delivery', desc: 'Agile sprints with transparent communication at every step.' },
                { icon: '🔒', title: 'Quality First', desc: 'Rigorous QA processes built into every stage of development.' },
                { icon: '🤝', title: 'Long-Term Partner', desc: 'We stay with you from launch through ongoing growth.' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1.0 + i * 0.1 }}
                  className="bg-white rounded-2xl p-6 border border-slate-200 shadow-card text-center hover:border-brand-blue/25 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="text-3xl mb-3">{item.icon}</div>
                  <h4 className="font-syne font-bold text-slate-900 text-sm mb-2">{item.title}</h4>
                  <p className="font-inter text-slate-500 text-xs leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </motion.div>
  );
}
